import { addWorkspaceHeaderIfNeeded } from "@/_helpers/workspace";
import { downloadFile } from "@/_helpers/download";
import type { Pagination } from "@/types";
import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { i18n } from "@/plugins/i18n";
import { axiosInstance } from "@/plugins/https";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { StoreModule } from "@/constants/store.ts";
import {
  type UserSettingsFilterItem,
  useUsersSettingsStore,
} from "@/_store/users-settings/users-settings.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { Service } from "@/constants/cloud-apps";
import { UserManualDecision, UserProtectionStatus } from "@/constants/users";
import { ProxyDirection } from "@/constants/email-proxy";
import type { EmailProxyDomain } from "@/_store/email-security/email-proxy.module";
import { getUserSettingsFilters } from "@/_store/users-settings/adapters";

export enum UserSettingsJoinedVia {
  INDIVIDUALLY = "INDIVIDUALLY",
  ADMIN = "ADMIN",
  GROUP = "GROUP",
  INBOUND_GATEWAY = "INBOUND_GATEWAY",
  CSV = "CSV",
}

export enum UserSettingsAppStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export interface UserListItemGroup {
  groupId: string;
  name: string;
  service: Service;
}

export interface UserSettingsProtectedApp {
  appName: Service;
  active: boolean;
  alias: string;
}

export interface UsersSettingsFilters {
  searchTerm: string;
  connectedApps?: Service[];
  status: UserSettingsAppStatus | null;
  protectedVia: UserSettingsFilterItem[];
  groups: UserSettingsFilterItem[];
  protectionStatus: UserProtectionStatus | null;
}

export interface UserSettingsListItem {
  userId: string;
  email: string;
  addedIndividually: boolean;
  isProtected: boolean;
  removable: boolean;
  groups: UserListItemGroup[];
  joinedSince: number;
  exceptedDateTime: number;
  exceptedBy: string;
  aliases: string[];
  cloudServices: Service[];
  exceptedUser: boolean;
  protectedVia: (UserSettingsJoinedVia | Service)[];
  connectedCloudApplications: UserSettingsProtectedApp[];
  // Set when an admin manually overrode the protection status coming from cloud sync
  manualDecision?: UserManualDecision | null;
}

export type JoinedViaGroups = { [key in Service]?: { total: number; [subkey: string]: number } };

export interface AddUsersPayload {
  users: Array<{ email: string; service: Service }>;
  workspaceId?: string;
}

export enum JoinedViaFilterType {
  JOINED_VIA = "joinedVia",
  JOINED_VIA_INBOUND_GATEWAY_DOMAINS = "joinedViaInboundGatewayDomains",
  JOINED_VIA_GROUPS = "joinedViaGroups",
}

export enum UserProtectionSyncStatus {
  SYNCHRONIZING = "synchronizing",
  SYNCHRONIZED = "synchronized",
  SYNC_ERROR = "syncError",
}

export interface JoinedViaFilterParams {
  [JoinedViaFilterType.JOINED_VIA]: string;
  [JoinedViaFilterType.JOINED_VIA_INBOUND_GATEWAY_DOMAINS]: string;
  [JoinedViaFilterType.JOINED_VIA_GROUPS]: string;
}

export interface JoinedViaFilterOptions extends JoinedViaGroups {
  general?: { [key in UserSettingsJoinedVia]?: number };
  inboundGateway?: Record<string, number>;
  protection: { protected: number; unprotected: number };
}

export interface UsersSettingsState {
  allUsers: number;
  pages: number;
  pagination: Pagination;
  users: UserSettingsListItem[];
  usersSyncing: boolean;
  usersProtectionStatus: {
    lastSync: number | null; // timestamp
    syncStatus: UserProtectionSyncStatus;
    protectedUsers: number;
    protectedUsersLimit: number;
    services: {
      service: Service;
      protectedUsers: number;
      totalUsers: number;
      syncStatus: UserProtectionSyncStatus;
    }[];
  };
  showSkeletonLoader: boolean;
  loading: boolean;
  joinedViaFilterOptions: JoinedViaFilterOptions;
  usersTabFilters: UsersSettingsFilters;
}

export const defaultUsersTabFilters = {
  searchTerm: "",
  connectedApps: [],
  protectedVia: [],
  groups: [],
  status: null,
  protectionStatus: null,
};

const defaultJoinedViaFilterOptions: JoinedViaFilterOptions = {
  protection: {
    protected: 0,
    unprotected: 0,
  },
};

interface UsersProtectionStatusResponseItem {
  lastSyncedAt: number;
  cloudServiceUserSyncStatus: UserProtectionSyncStatus;
  protectedUsers: number;
  identifiedUsers: number;
}

type WorkspaceProtectionStatus = {
  protectedUsers: number;
  userProtectionLimit: number;
};

type UsersProtectionStatusResponse = {
  [key in Service]: UsersProtectionStatusResponseItem;
} & { workspace: WorkspaceProtectionStatus };

const defaultUsersState = {
  allUsers: 0,
  pages: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  users: [], // we are caching items to handle case with selection and switching pages
  showSkeletonLoader: true,
  usersSyncing: false,
  usersProtectionStatus: {
    syncStatus: UserProtectionSyncStatus.SYNCHRONIZED,
    lastSync: null,
    protectedUsers: 0,
    protectedUsersLimit: 0,
    services: [],
  },
  loading: true,
  joinedViaFilterOptions: defaultJoinedViaFilterOptions,
  usersTabFilters: {
    ...defaultUsersTabFilters,
  },
};

export const useUsersSettingsTabStore = defineStore(StoreModule.USERS_SETTINGS_TAB, {
  state: (): UsersSettingsState => ({ ...defaultUsersState }),
  getters: {
    defaultUsersTabFilters() {
      return defaultUsersTabFilters;
    },
  },
  actions: {
    updateFilters(newFilters: UsersSettingsFilters) {
      this.usersTabFilters = newFilters;
    },
    async getUsersProtectionStatus() {
      const request = {
        ...api.getProtectedUsersCount(),
      };

      const response = await axiosInstance.request<UsersProtectionStatusResponse>(request);
      const { workspace, ...serviceEntries } = response.data;
      const { protectedUsers, userProtectionLimit } = workspace || {
        protectedUsers: 0,
        protectedUsersLimit: 0,
      };
      this.usersProtectionStatus.protectedUsers = protectedUsers;
      this.usersProtectionStatus.protectedUsersLimit = userProtectionLimit;

      let lastSyncedAt = 0;
      let syncStatus = UserProtectionSyncStatus.SYNCHRONIZED;

      this.usersProtectionStatus.services = Object.entries(serviceEntries).map(([key, value]) => {
        const serviceData = value as UsersProtectionStatusResponseItem;

        if (serviceData.lastSyncedAt > lastSyncedAt) {
          lastSyncedAt = serviceData.lastSyncedAt;
        }

        if (serviceData.cloudServiceUserSyncStatus === UserProtectionSyncStatus.SYNCHRONIZING) {
          syncStatus = UserProtectionSyncStatus.SYNCHRONIZING;
        } else if (serviceData.cloudServiceUserSyncStatus === UserProtectionSyncStatus.SYNC_ERROR) {
          syncStatus = UserProtectionSyncStatus.SYNC_ERROR;
        }
        return {
          service: key as Service,
          protectedUsers: serviceData.protectedUsers,
          totalUsers: serviceData.identifiedUsers,
          syncStatus: serviceData.cloudServiceUserSyncStatus,
        };
      });

      this.usersProtectionStatus.lastSync = lastSyncedAt;
      this.usersProtectionStatus.syncStatus = syncStatus;
    },
    async toggleUserProtectionStatus(payload: {
      protected: boolean;
      users: UserSettingsListItem[];
    }) {
      this.loading = true;
      const { users } = payload;
      const userIds = users.map(({ userId }: { userId: string }) => userId);
      const request = {
        ...api.userProtectionStatus(),
        method: "PUT",
        data: {
          userIds,
          enabled: payload.protected,
        },
      };

      try {
        await axiosInstance.request(request);
        this.getUsers(false);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getUsers(showSkeletonLoader = true) {
      const filters = this.usersTabFilters;

      const denormalizedFilters = getUserSettingsFilters(filters);

      const request = {
        ...api.users(),
        params: {
          ...this.pagination,
          ...denormalizedFilters,
          email: filters.searchTerm,
        },
      };
      this.loading = true;
      if (showSkeletonLoader) {
        this.showSkeletonLoader = true;
      }
      try {
        const response = await axiosInstance.request(request);
        const data = response.data;
        // API occasionally omits protectedVia; default to [] to prevent
        // downstream .includes() calls from throwing on undefined
        this.users = data.items.map((item: UserSettingsListItem) => ({
          ...item,
          protectedVia: item.protectedVia ?? [],
        }));
        this.joinedViaFilterOptions = data.groupSummary;
        this.allUsers = data.total;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.showSkeletonLoader = false;
      }
    },
    searchProtectedUsers() {
      const filters = this.usersTabFilters;

      const request = {
        ...api.users(),
        params: {
          searchTerm: filters.searchTerm,
          pagination: { page: 0, pageSize: 10 },
        },
      };
      return axiosInstance.request(request);
    },
    /**
     * This action will search users in cloud services
     * @param email
     * @returns {Promise<AxiosResponse}
     */
    searchUsers(email: string) {
      const request = {
        ...api.searchUsers(email),
      };
      return axiosInstance.request(request);
    },
    /**
     * This action will search already synchronized users
     * @returns {Promise<AxiosResponse}
     */
    searchSyncedUsers() {
      const filters = this.usersTabFilters;

      const request = {
        ...api.users(),
        params: {
          ...this.pagination,
          searchTerm: filters.searchTerm,
        },
      };
      return axiosInstance.request(request);
    },
    applyPaginationChange(payload: Pagination) {
      const pagination = {
        page: payload.page,
        pageSize: payload.pageSize,
      };
      this.pagination = pagination;
      this.getUsers();
    },
    async addUsers({ users, workspaceId }: AddUsersPayload) {
      const snackbarStore = useSnackbarStore();
      const usersSettingsStore = useUsersSettingsStore();
      const request = {
        ...api.addUsers,
        data: users,
      };
      addWorkspaceHeaderIfNeeded(request, workspaceId);
      if (users.length > 30) {
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersAreAdding"));
      }

      try {
        await axiosInstance.request(request);
        this.getUsers(false);
        usersSettingsStore.init(false);
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    async syncUsers() {
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.syncUsers,
      };
      try {
        await axiosInstance.request(request);
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersSyncStarted"));
      } catch (error) {
        console.error(error);
      }
    },
    async removeUsers(payload: { users: UserSettingsListItem[] }) {
      const usersSettingsStore = useUsersSettingsStore();
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.removeUser({
          userIds: payload.users.map(({ userId }: { userId: string }) => userId),
        }),
      };

      try {
        await axiosInstance.request(request);
        this.getUsers(false);
        usersSettingsStore.init(false);
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    async addAllUsers(payload: DialogDataConfig) {
      const usersSettingsStore = useUsersSettingsStore();
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.addAllUsersToProtection(payload.item.service),
      };

      try {
        await axiosInstance.request(request);
        usersSettingsStore.init(false);
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    resetState() {
      this.$reset();
    },
    async addToException(payload: DialogDataConfig) {
      const snackbarStore = useSnackbarStore();
      const { email, userId } = payload.item;
      const request = {
        ...api.addExceptions,
        data: [
          {
            email,
            userId,
            service: "coronet",
          },
        ],
      };
      try {
        await axiosInstance.request(request);
        this.getUsers();
        useUsersSettingsStore().init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    async importUsersFromCsv(payload: Blob | File) {
      const snackbarStore = useSnackbarStore();
      const data = new FormData();
      data.append("file", payload);
      const request = {
        ...api.importUsersViaCsv(data),
      };

      const response = await axiosInstance.request<{
        identifiedUsers: number;
        importedAsProtected: boolean;
      }>(request);
      const { identifiedUsers, importedAsProtected } = response.data;
      const messageKey = importedAsProtected ? "csvImportSuccess" : "csvImportUnprotected";
      snackbarStore.addGenericSuccess(
        i18n.global.t(`snackbar.messages.user.${messageKey}`, { identifiedUsers })
      );
    },
    async downloadUsersExampleCsv() {
      const request = {
        ...api.getUsersExampleCsv(),
      };

      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = "UsersExample.csv";
        downloadFile(blob, fileName);
      } catch (error) {
        console.error(error);
      }
    },
    async removeException(payload: DialogDataConfig) {
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.removeException(payload.item.userId),
      };
      try {
        await axiosInstance.request(request);
        this.getUsers();
        useUsersSettingsStore().init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (e) {
        console.error(e);
      }
    },
    async exportCsv() {
      const snackbarStore = useSnackbarStore();
      const filters = this.usersTabFilters;

      const denormalizedFilters = getUserSettingsFilters(filters);
      const request = {
        ...api.exportUsersCsv,
        params: {
          ...denormalizedFilters,
        },
      };
      try {
        await axiosInstance.request(request);
        this.getUsers();
        snackbarStore.addGenericSuccess(
          i18n.global.t("snackbar.messages.general.exportCsvStarted", {
            pageName: i18n.global.t("activityLogs.title"),
          })
        );
      } catch (e) {
        console.error(e);
      }
    },
    async getProxyDomains(
      direction: ProxyDirection = ProxyDirection.INBOUND
    ): Promise<EmailProxyDomain[]> {
      const request = {
        ...api.emailProxyDomains(),
        params: {
          direction,
        },
      };

      try {
        const { data } = await axiosInstance.request<EmailProxyDomain[]>(request);

        return data;
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    setUsersPagination(pagination: Pagination) {
      this.pagination = pagination;
    },
  },
});
